import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { AnimatedSection } from './AnimatedSection';
import { trackCalendlyConversion } from './GoogleTagLoader';
export function CaseStudiesSection() {
  const caseStudies = [{
    agency: 'SF AI Builders',
    headline: 'Cut no-show rates in half with personalized outreach',
    description: 'Running weekly AI builder nights meant hundreds of RSVPs but unpredictable attendance. Turnout automated personalized follow-ups to every registrant, drastically reducing no-shows and building a more consistent community.',
    metrics: [{
      value: '52%→26%',
      label: 'No-Show Rate'
    }, {
      value: '3x',
      label: 'Repeat Attendance'
    }],
    logo: 'https://cdn.magicpatterns.com/uploads/s8Y6nzsiuRyEvP2FHhV2kh/33c952_4fddab6300d04fb0954d3cb229081395~mv2.png',
    link: 'https://www.phoxevents.com/about',
    initial: 'S'
  }, {
    agency: 'DevRel Weekly',
    headline: 'Stopped manual DMs to 200 registrants every week',
    description: 'Sending individual reminders to 200 developers took hours of manual work. Turnout replaced their entire workflow, sending personalized messages automatically while they focus on the content.',
    metrics: [{
      value: '12→1',
      label: 'Outreach Workflows'
    }, {
      value: '200+',
      label: 'Messages Personalized/Week'
    }],
    logo: 'https://cdn.magicpatterns.com/uploads/fJSy8iCi2m32tkwWiRKNNp/cropped-BUFLUS_logo-removebg-preview_(1).png',
    link: 'https://buflusevents.com/',
    initial: 'D'
  }, {
    agency: 'Founders Circle',
    headline: 'Runs 10 events a month without a community coordinator',
    description: 'Scaling from 2 to 10 monthly dinners required a massive increase in attendee coordination. Turnout handles all the follow-ups and confirmations automatically, keeping attendance high without adding headcount.',
    metrics: [{
      value: '0',
      label: 'Missed Follow-ups'
    }, {
      value: '10',
      label: 'Events/Month'
    }],
    logo: 'https://cdn.magicpatterns.com/uploads/4EWdPChyEYXuKDWKCzpKBL/logo.png',
    logoBg: 'bg-gray-900',
    link: 'https://ondotexperience.com/',
    initial: 'F'
  }];
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.15
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };
  return <section id="case-studies" className="py-24 bg-gray-50" data-id="element-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-41">
        <AnimatedSection data-id="element-42">
          <div className="text-center mb-16" data-id="element-43">
            <p className="text-sm font-semibold text-indigo-600 tracking-wide uppercase mb-3" data-id="element-44">
              Real Results
            </p>
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl" data-id="element-45">
              They Chose Turnout Instead of Hiring
            </h2>
            <p className="mt-4 max-w-2xl text-xl text-gray-500 mx-auto" data-id="element-46">
              Real communities that scaled without adding headcount.
            </p>
          </div>
        </AnimatedSection>

        <motion.div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} data-id="element-47">
          {caseStudies.map((study, index) => <motion.a key={index} href={study.link} target="_blank" rel="noopener noreferrer" variants={cardVariants} whileHover={{
          y: -4
        }} className="group relative bg-white rounded-xl overflow-hidden border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300 flex flex-col h-full cursor-pointer" data-id="element-48">
              {/* Top accent line */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-indigo-600 transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300" data-id="element-49" />

              <div className="p-8 flex-1 flex flex-col relative z-10" data-id="element-50">
                {/* Header: Logo + Brand Name */}
                <div className="flex items-center justify-between mb-6" data-id="element-51">
                  <div className="flex items-center gap-3" data-id="element-52">
                    {study.logo ? <div className={`w-8 h-8 rounded overflow-hidden flex items-center justify-center flex-shrink-0 ${study.logoBg || 'bg-white'} border border-gray-100 p-0.5`} data-id="element-53">
                        <img src={study.logo} alt={study.agency} className="w-full h-full object-contain" data-id="element-54" />
                      </div> : <div className="w-8 h-8 rounded bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold" data-id="element-55">
                        {study.agency.charAt(0)}
                      </div>}
                    <span className="text-xs font-bold text-gray-400 uppercase tracking-widest" data-id="element-56">
                      {study.agency}
                    </span>
                  </div>
                </div>

                {/* Narrative Headline */}
                <h3 className="text-xl font-bold text-gray-900 mb-6 leading-tight group-hover:text-indigo-600 transition-colors duration-200" data-id="element-57">
                  {study.headline}
                </h3>

                {/* Metrics Row */}
                <div className="grid grid-cols-2 gap-4 mb-6 pt-6 border-t border-gray-100 relative" data-id="element-58">
                  {/* Watermark behind metrics */}
                  <div className="absolute -right-2 -bottom-4 text-9xl font-black text-gray-50 opacity-[0.03] select-none pointer-events-none font-serif leading-none z-0" data-id="element-59">
                    {study.initial}
                  </div>

                  {study.metrics.map((metric, i) => <div key={i} className="relative z-10" data-id="element-60">
                      <p className="text-3xl font-bold text-indigo-600 tracking-tight" data-id="element-61">
                        {metric.value}
                      </p>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mt-1" data-id="element-62">
                        {metric.label}
                      </p>
                    </div>)}
                </div>

                {/* Description */}
                <p className="text-gray-600 text-sm leading-relaxed mb-6 flex-1" data-id="element-63">
                  {study.description}
                </p>

                {/* Read More Link */}
                <div className="flex items-center text-indigo-600 font-semibold text-sm group-hover:gap-2 transition-all duration-200 mt-auto" data-id="element-64">
                  <span data-id="element-65">Read full story</span>
                  <ArrowUpRight className="h-4 w-4 ml-1 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-200" data-id="element-66" />
                </div>
              </div>
            </motion.a>)}
        </motion.div>

        <AnimatedSection delay={0.5} data-id="element-67">
          <div className="mt-16 text-center" data-id="element-68">
            <a href="https://calendly.com/dan-at-turnout/turnout-intro-" target="_blank" rel="noopener noreferrer" onClick={trackCalendlyConversion} className="inline-flex items-center px-8 py-4 border border-transparent text-lg font-medium rounded-xl shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 transition-colors duration-300" data-id="element-69">
              See How It Works
            </a>
          </div>
        </AnimatedSection>
      </div>
    </section>;
}